// third party imports
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// internal rebet imports
import "./stylesBetTable.css";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { handleExpiredTokenError } from "../../../../utils/ErrorUtils";
import { formatLargeNumbers } from "../../../../utils/NumberFormatter";

// component imports
import DropDown from "../../../CommonComponents/DropDown/DropDown";
import BetRow from "./BetRow/BetRow";
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import EditBetPopup from "./EditBetPopup/EditBetPopup";

// asset imports
import SearchIcon from "../../../../assets/icons/ic_search.png";
import { Input, Pagination, Popover, Table } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  BET_STATUS,
  BET_TYPES,
  KPI_CARD_BACKGROUND,
} from "../../../../utils/Constants";
import EditIcon from "../../../../assets/icons/ic_edit_green.png";
import SaveButton from "../../../CommonComponents/SaveButton/SaveButton";
import KpiCard from "../../../CommonComponents/Dashboard/KpiCard/KpiCard";
import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import { setSeconds } from "date-fns";

const BetTable = ({ userData }) => {
  // non-state variables

  const todayRange = [dayjs().startOf("day"), dayjs().endOf("day")];
  const [betsDates, setBetsDates] = useState(null);
  const [summary, setSummary] = useState({
    total_wager: 0,
    total_payout: 0,
    total_net_revenue: 0,
  });

  const SEARCH_OPTIONS = {
    STRAIGHT: "straight",
    GROUP: "group",
    P2P: "p2p",
  };

  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );
  const navigate = useNavigate();

  // state variables
  const [searchValue, setSearchValue] = useState("");
  const [searchBetType, setSearchBetType] = useState("");
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState({});
  const [betsToShowInTable, setBetsToShowInTable] = useState([]);
  const [isApiRunning, setIsApiRunning] = useState(false);
  const [isEditBetPopupVisible, setIsEditBetPopupVisible] = useState(false);
  const [selectedBetData, setSelectedBetData] = useState({});
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 20,
    total_results: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [betsCache, setBetsCache] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // use effects
  useEffect(() => {
    getUsersBets(1, true, todayRange, false, true);
  }, [userData]);

  const handleDateChange = (queryString) => {
    getUsersBets(1, true, queryString, true, false);
  };

  const formatTimestamp = (timestampStr) => {
    dayjs.extend(utc);

    const timestamp = Number(timestampStr);
    return dayjs.unix(timestamp).utc().format("DD MMMM YYYY, HH:mm:ss");
  };

  const handleWager = (record) => {
    if (record?.money_type === "") return "";

    return formatLargeNumbers(record?.wager_amount, 2);
  };

  const handlePayout = (record) => {
    if (record?.payout === "" || isNaN(record?.payout)) return "0.00";

    return record?.payout
      ? formatLargeNumbers(record?.payout, 2)
      : record?.status === BET_STATUS?.LOST
      ? "0.00"
      : record?.status === BET_STATUS?.CANCELLED
      ? formatLargeNumbers(record?.wager_amount, 2)
      : "-";
  };

  const handleBetType = (record) => {
    if (record?.bet_type === "1") {
      return BET_TYPES?.STRAIGHT;
    } else if (record?.bet_type === "2") {
      return BET_TYPES?.P2P;
    } else if (
      (record?.bet_type === "5" && record?.wager_amount === "0.00") ||
      record?.is_multibet
    ) {
      return BET_TYPES?.GROUP;
    } else {
      return BET_TYPES?.PARLAY;
    }
  };

  const handleBetStatus = (record) => {
    if (record?.cancelled) {
      return "Cancelled";
    } else if (record?.void_factor === "1") {
      return "Pushed";
    } else if (record?.won === "-1") {
      return "Unsettled";
    } else if (record?.won === "0") {
      return "Lost";
    } else {
      return "Won";
    }
  };

  const handleBetStatusClassName = (record) => {
    return handleBetStatus(record) === BET_STATUS?.WON
      ? "win-type-text-background-good"
      : handleBetStatus(record) === BET_STATUS?.PUSHED ||
        handleBetStatus(record) === BET_STATUS?.CANCELLED
      ? "win-type-text-background-neutral"
      : handleBetStatus(record) === BET_STATUS?.LOST
      ? "win-type-text-background-negative"
      : handleBetStatus(record) === BET_STATUS?.UNSETTLED
      ? "value-text-background-unsettled"
      : "bet-cell-label";
  };

  const handleBetStatusTextClassName = (record) => {
    return handleBetStatus(record) === BET_STATUS?.WON
      ? "win-type-text-good"
      : handleBetStatus(record) === BET_STATUS?.PUSHED ||
        handleBetStatus(record) === BET_STATUS?.CANCELLED
      ? "win-type-text-neutral"
      : handleBetStatus(record) === BET_STATUS?.LOST
      ? "win-type-text-negative"
      : handleBetStatus(record) === BET_STATUS?.UNSETTLED
      ? "value-text-unsettled"
      : "bet-cell-label";
  };

  const convertDecimalToAmerican = (decimalOdds) => {
    let americanOdds;

    if (decimalOdds > 2.0) {
      const oddsValue = Math.round((decimalOdds - 1) * 100);
      americanOdds = "+" + formatLargeNumbers(oddsValue, 0);
    } else if (decimalOdds < 2.0) {
      const oddsValue = Math.round(-(100 / (1 - decimalOdds)));
      americanOdds = oddsValue > 0 ? "-" + formatLargeNumbers(oddsValue, 0) : oddsValue.toString();
    } else {
      americanOdds = "+100";
    }

    return americanOdds;
  };

  const handleBetOdds = (bet_data) => {
    const betType = handleBetType(bet_data);
    if (betType === BET_TYPES?.PARLAY) {
      return convertDecimalToAmerican(bet_data?.combined_odds);
    } else {
      const betOdds = bet_data?.selection?.market?.outcome?.find(
        (outcome) => outcome?.chosen === "1"
      )?.odds;
      return convertDecimalToAmerican(betOdds);
    }
  };

  const handlePotentialPayout = (record) => {
    const betType = handleBetType(record);
    if (betType === BET_TYPES?.PARLAY) {
      const combinedOdds = record?.combined_odds;
      const wagerAmount = record?.wager_amount;
      const payoutAmount = parseFloat(combinedOdds) * parseFloat(wagerAmount);

      return formatLargeNumbers(payoutAmount, 2);
    } else {
      const betOdds = record?.selection?.market?.outcome?.find(
        (outcome) => outcome?.chosen === "1"
      )?.odds;
      const wagerAmount = record?.wager_amount;
      const payoutAmount = parseFloat(betOdds) * parseFloat(wagerAmount);

      if (isNaN(payoutAmount)) return "0.00";

      return formatLargeNumbers(payoutAmount, 2);
    }
  };

  const handleSelection = (record) => {
    return record?.selection?.market?.outcome[0]?.name ?? "-";
  };

  const handleMarket = (record) => {
    return record?.selection?.market?.market_name ?? "-";
  };

  const handleSelectionParent = (record) => {
    if (!record) return "-";

    return record?.selection?.market?.outcome[0]?.name ?? "-";
  };

  const handleMarketParent = (record) => {
    if (!record) return "-";

    return record?.selection?.market?.market_name ?? "-";
  };

  const columns = [
    {
      title: "Date / Time",
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      render: (text, record) =>
        text === "" ? "" : formatTimestamp(record?.created_at),
    },
    {
      title: "ID",
      dataIndex: "bet_id",
      key: "bet_id",
      width: 300,
    },
    {
      title: "Market",
      dataIndex: "market",
      key: "market",
      width: 250,
    },
    {
      title: "Selection",
      dataIndex: "selection",
      key: "selection",
      width: 200,
    },
    {
      title: "Money Type",
      dataIndex: "money_type",
      key: "money_type",
      width: 150,
    },
    {
      title: "Pre match/Live",
      dataIndex: "prematch_live",
      key: "prematch_live",
      width: 150,
    },
    {
      title: "Wager",
      dataIndex: "wager",
      key: "wager",
      width: 150,
      sorter: (a, b) => a.wager_amount - b.wager_amount,
      render: (text, record) => handleWager(record),
    },
    {
      title: "Payout",
      dataIndex: "payout",
      key: "payout",
      width: 150,
      sorter: (a, b) => a.payout - b.payout,
      render: (text, record) => {
        return record.display_status === "Cashed Out" ? record.cashout_amount : handlePayout(record);
      },
    },
    {
      title: "Status",
      dataIndex: "display_status",
      key: "display_status",
      width: 150,
      render: (text, record) => {
        return text === "" ? (
          ""
        ) : (
          <div className={`sports-bet-status-container ${text?.split(" ").join("-").toLowerCase()}`}>
            <span className={`sports-bet-status ${text?.split(" ").join("-").toLowerCase()}`}>
              {text}
            </span>
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "bet_type",
      key: "bet_type",
      width: 120,
      render: (text, record) => {
        return text === "" ? "" : handleBetType(record);
      },
    },
    {
      title: "Odds",
      dataIndex: "odds",
      key: "odds",
      width: 100,
      render: (text, record) => {
        return text === "" ? "" : handleBetOdds(record);
      },
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      width: 120,
      render: (text, record) => {
        return text === "" ? (
          ""
        ) : (
          <button
            className={`edit-user-button sports-bets-edit-button ${
              record.finalized === "0" ? "" : "disabled"
            }`}
            onClick={() => handleEditBetPressed(record)}
            disabled={record.finalized === "0" ? false : true}
          >
            <img className="button-icon" src={EditIcon} alt="edit icon" />
          </button>
        );
      },
    },
    {
      title: "Potential Payout",
      dataIndex: "potential_payout",
      key: "potential_payout",
      width: 180,
      render: (text, record) => {
        return text === "" ? "" : handlePotentialPayout(record);
      },
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      width: 100,
      render: (text) => {
        return formatLargeNumbers(text, 2);
      },
    },

  ];
  // api functions
  const getUsersBets = async (
    page = 1,
    refresh = false,
    queryString = todayRange,
    isQueryString = true,
    allTime = false
  ) => {
    if (!userData) return;
    setIsApiRunning(true);

    let startDate = "";
    let endDate = "";

    if (isQueryString) {
      let formattedQueryString = queryString.split("&");
      startDate = dayjs(formattedQueryString[0]).format("YYYY-MM-DD");
      endDate = dayjs(formattedQueryString[1]).format("YYYY-MM-DD");
    } else if (allTime) {
      startDate = null;
      endDate = null;
    } else {
      startDate = queryString[0]?.format("YYYY-MM-DD");
      endDate = queryString[1]?.format("YYYY-MM-DD");
    }

    const cacheKey = page;

    if (betsCache[cacheKey] && !refresh) {
      const cached = betsCache[cacheKey];
      setBetsToShowInTable(cached.processedBets);
      setPagination(cached.pagination);
      setIsApiRunning(false);
      return;
    }

    let params = {
      page: page,
      user_id: userData?.user_id,
      start_date: startDate,
      end_date: endDate,
    };

    if (refresh) {
      params = {
        ...params,
      };
    } else {
      params = {
        ...params,
        last_evaluated_key: lastEvaluatedKey,
      };
    }

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GET_USER_BETS,
        params
      );

      const all_bets = [...response?.data?.data?.items];
      const all_bets_sorted = all_bets?.sort((a, b) => {
        return parseFloat(b?.created_at) - parseFloat(a.created_at);
      });
      const all_unique_bets_sorted = Array.from(
        new Map(all_bets_sorted.map((bet) => [bet?.bet_id, bet])).values()
      );

      console.log("all_unique_bets_sorted", all_unique_bets_sorted);

      const processedBets = all_unique_bets_sorted.flatMap((bet) =>
        Array.isArray(bet.bets) &&
        (bet.wager_amount === "0.00" || bet.wager_amount === "0")
          ? bet.bets.map((subBet, index) => {
              let newSubBet = {
                ...subBet,
                balance: index === 0 ? bet.balance : "-",
                bet_id: `${bet.bet_id}-${index + 1}`,
                money_type: bet.money_type,
                odds_provider: bet.odds_provider,
                display_status: handleBetStatus(subBet),
                prematch_live: bet.prematch_live,
                bet_type: bet.bet_type,
                created_at: bet.created_at,
                is_multibet: true,
                original_bet_id: bet.bet_id,
                group_bet_index: index,
                finalized:
                  subBet.finalized !== undefined
                    ? subBet.finalized
                    : bet.finalized,
                won: subBet.won !== undefined ? subBet.won : bet?.won,
                canceled:
                  subBet.cancelled !== undefined
                    ? subBet.cancelled
                    : bet?.cancelled,
                void_factor:
                  subBet.void_factor !== undefined
                    ? subBet.void_factor
                    : bet?.void_factor,
                payout:
                  subBet.payout !== undefined ? subBet.payout : bet?.payout,
              };

              return newSubBet;
            })
          : [bet]
      );

      const processedBetsWithMarketAndSelection = processedBets.map((bet) => {
        const bets = bet?.bets || null;
        const children = bets?.slice(1).map((child, index) => {
          return {
            bet_id: "",
            custom_bet_id: `${bet.bet_id}-${index + 1}`,
            created_at: "",
            money_type: "",
            pre_match_live: "",
            wager_amount: "",
            payout: "",
            status: "",
            bet_type: "",
            odds: "",
            edit: "",
            potential_payout: "",
            balance: "",
            market: handleMarket(child),
            selection: handleSelection(child),
            isChild: true,
          };
        });
        return {
          ...bet,
          custom_bet_id: bet?.bet_id,
          market:  bets ? handleMarketParent(bet?.bets?.[0]) : handleMarketParent(bet),
          selection: bets ? handleSelectionParent(bet?.bets?.[0]) : handleSelectionParent(bet),
          children: children,
          isChild: false,
        };
      });

      const paginationData = {
        current_page: response?.data?.data?.pagination?.current_page,
        page_size: response?.data?.data?.pagination?.page_size,
        total_results: response?.data?.data?.pagination?.total_results,
        total_pages: response?.data?.data?.pagination?.total_pages,
        has_next: response?.data?.data?.pagination?.has_next,
        has_prev: response?.data?.data?.pagination?.has_prev,
      };

      if (refresh) {
        const totalWager = response?.data?.data?.totals?.total_wagers ?? 0;
        const totalPayout = response?.data?.data?.totals?.total_payouts ?? 0;
        const totalNetRevenue = totalWager - totalPayout;

        setSummary({
          total_wager: totalWager,
          total_payout: totalPayout,
          total_net_revenue: totalNetRevenue,
        });
      }
      setBetsToShowInTable(processedBetsWithMarketAndSelection);
      setLastEvaluatedKey(response?.data?.data?.last_evaluated_key);
      setPagination(paginationData);

      setBetsCache((prev) => ({
        ...prev,
        [cacheKey]: {
          processedBets,
          summary,
          pagination: paginationData,
        },
      }));
    } catch (error) {
      console.log("error fetching users bets", error);
      handleExpiredTokenError(navigate, error?.response);
    } finally {
      setIsApiRunning(false);
    }
  };

  const searchBet = async () => {
    setIsApiRunning(true);

    const params = {
      user_id: userData?.user_id,
      bet_id: searchValue,
      bet_type:
        searchBetType === SEARCH_OPTIONS?.STRAIGHT
          ? "1"
          : searchBetType === SEARCH_OPTIONS?.GROUP
          ? "5"
          : "2",
    };

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GET_BET_BY_ID,
        params
      );

      const data = response?.data?.data;

      const processedBetsWithMarketAndSelection = [data].map((bet) => {
        const bets = bet?.bets || null;
        const children = bets?.slice(1).map((child, index) => {
          return {
            bet_id: "",
            custom_bet_id: `${bet.bet_id}-${index + 1}`,
            created_at: "",
            money_type: "",
            pre_match_live: "",
            wager_amount: "",
            payout: "",
            status: "",
            bet_type: "",
            odds: "",
            edit: "",
            potential_payout: "",
            market: handleMarket(child),
            selection: handleSelection(child),
            isChild: true,
          };
        });
        return {
          ...bet,
          custom_bet_id: bet?.bet_id,
          market:  bets ? handleMarketParent(bet?.bets?.[0]) : handleMarketParent(bet),
          selection: bets ? handleSelectionParent(bet?.bets?.[0]) : handleSelectionParent(bet),
          children: children,
          isChild: false,
        };
      });

      setBetsToShowInTable(processedBetsWithMarketAndSelection);
      setPagination({
        current_page: 1,
        page_size: 10,
        total_results: 1,
        total_pages: 1,
        has_next: false,
        has_prev: false,
      });

      setIsApiRunning(false);
    } catch (error) {
      console.log("error searching for bet", error?.response);
      setIsApiRunning(false);
      handleExpiredTokenError(navigate, error?.response);
    }
  };

  // helper functions
  const handleSearchValueChange = (event) => {
    setSearchValue(event?.target?.value);
  };

  const handleChangeSearchBetType = (value) => {
    setSearchBetType(value);
  };

  const handleSearchPressed = () => {
    searchBet();
  };

  const handleEditBetPressed = (bet_data) => {
    setSelectedBetData(bet_data);
    setIsEditBetPopupVisible(true);
  };

  const refreshBets = () => {
    setTimeout(() => {
      getUsersBets(1, true, todayRange, false, true);
    }, 1500);
  };

  const handlePaginationChange = (page) => {
    let allTimeParams = betsDates ? false : true;
    let queryStringParams = betsDates ? betsDates : todayRange;

    getUsersBets(page, false, queryStringParams, false, allTimeParams);
  };

  return (
    <div className="sports-bets-container">
      <div className="sports-bets-header">
        <div className="kpi-cards-container">
          <KpiCard
            isApiRunning={isApiRunning}
            title="Total Wager"
            value={summary?.total_wager}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isApiRunning}
            title="Total Payout"
            value={summary?.total_payout}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isApiRunning}
            title="Net Revenue"
            value={summary?.total_net_revenue}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
        <CustomDatePicker
          getData={handleDateChange}
          dates={betsDates}
          setDates={setBetsDates}
          showPresets={true}
          startRange="Today"
        />
      </div>

      <div className="sports-bets-table-container">
        <div className="head-controls sports-bets-table-header-container">
          <div
            className="header-text-container"
            style={{ width: "max-content" }}
          >
            <p className="header-text">User's Sports Bets</p>
          </div>

          <div className="sports-bets-search-container">
            <Input
              type="text"
              placeholder="Search bets..."
              className="bet-searchInput"
              value={searchValue}
              onChange={handleSearchValueChange}
            />

            <div className="drop-down-container">
              <DropDown
                drop_down_options={SEARCH_OPTIONS}
                selected_value={searchBetType}
                set_selected_value={handleChangeSearchBetType}
              />
            </div>

            <button
              className="search-button"
              style={{ margin: "0px" }}
              onClick={handleSearchPressed}
            >
              <img className="search-icon" src={SearchIcon} alt="search-icon" />
            </button>
          </div>
        </div>

        <div
          className="table-container sports-bets-table"
          style={{ overflowY: "hidden" }}
        >
          {isApiRunning ? (
            <RebetLoader height={"100px"} width={"100px"} />
          ) : (
            <div className="data-rows-container">
              <Table
                dataSource={betsToShowInTable}
                columns={columns}
                scroll={{ x: 1000, y: 400 }}
                pagination={false}
                rowKey={(record) => record?.custom_bet_id}
                expandable={{
                  expandedRowKeys,
                  // expandIconColumnIndex: 12,
                  onExpand: (expanded, record) => {
                    if (expanded) {
                      setExpandedRowKeys([
                        ...expandedRowKeys,
                        record.custom_bet_id,
                      ]);
                    } else {
                      setExpandedRowKeys(
                        expandedRowKeys.filter(
                          (key) => key !== record.custom_bet_id
                        )
                      );
                    }
                  },
                }}
                rowClassName={(record) => {
                  if (expandedRowKeys.includes(record.custom_bet_id)) {
                    return "expanded-row";
                  }
                  return record?.isChild ? "child-row" : "parent-row";
                }}
              />
            </div>
          )}

          <EditBetPopup
            isVisible={isEditBetPopupVisible}
            onClosePressed={() => setIsEditBetPopupVisible(false)}
            betData={selectedBetData}
            refreshBets={refreshBets}
          />
        </div>
        {!isApiRunning && pagination?.total_results > 10 && (
          <div className="sports-bets-pagination-container">
            <Pagination
              current={pagination.current_page}
              total={pagination.total_results}
              pageSize={pagination.page_size}
              showSizeChanger={false}
              onChange={(page) => {
                if (
                  page === pagination.current_page - 1 ||
                  page === pagination.current_page + 1
                ) {
                  handlePaginationChange(page);
                }
              }}
              itemRender={(page, type, originalElement) => {
                if (type === "page") {
                  return (
                    <a
                      rel="nofollow"
                      disabled={page > pagination.current_page + 1}
                      className={`${
                        page > pagination.current_page + 1 ? "disabled" : ""
                      }`}
                    >
                      {page}
                    </a>
                  );
                }
                return originalElement;
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BetTable;
